import { Box, Typography, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useRecoilState } from "recoil";

import { CustomButton } from "@/components/Button";
import { RegistrationActions, RegistrationBack } from "@/components/property_registration/RegistrationActions";
import { CustomForm } from "@/components/property_registration/RegistrationInputs";
import { CustomFormLabel } from "@/components/property_registration/RegistrationInputs";
import { DatePickerComponent } from "@/components/property_registration/RegistrationInputs";
import { Toast } from "@/components/Toast";
import theme from "@/theme";
import { PropertyRegistrationState } from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  content: {
    width: 640,
    [theme.breakpoints.down("sm")]: {
      width: 342,
    },
    margin: "8px  auto 0px auto",
  },
  block: {
    marginBottom: 24,
  },
  blockTitle: {
    color: theme.palette.text.primary,
    marginBottom: 8,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      borderBottom: "none",
    },
  },
}));

interface AddNewOwnBuildingPropertyRoomPageProps {
  onNext: () => void;
  onBack: () => void;
  validate: () => Promise<boolean>;
}

export const prefecture_unselected = "";

const AddNewOwnBuildingPropertyRoomPage: React.FC<AddNewOwnBuildingPropertyRoomPageProps> = ({
  onNext,
  onBack,
  validate,
}) => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();
  const [formData] = useRecoilState(PropertyRegistrationState);
  const watchValues = useWatch({
    name: ["type_of_the_income_of_the_beginning_of_contract", "starts_at", "starts_at_day", "ends_at"],
    control: control,
  });

  // サーバーエラー
  const [serverError, setSeverError] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");

  const toMonthPickerDate = useCallback((value: unknown, fallback: Date | null) => {
    // MEMO: 以前はfallbackに「当月」等を渡して未入力でも自動入力していたが、未入力開始に変更（2026-01-02）
    if (!value) return fallback;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? fallback : value;
    const parsed = dayjs(value as never);
    return parsed.isValid() ? parsed.toDate() : fallback;
  }, []);

  const handleNext = async () => {
    const isValid = await validate();
    const data = getValues();
    if (isValid) {
      console.log("isValid in cashflow");
      console.log(data);
      onNext();
    }
  };

  const handleClose = useCallback(() => {
    setSeverError("");
  }, []);

  useEffect(() => {
    if (dayjs(watchValues.ends_at).isBefore(dayjs(watchValues.starts_at))) {
      setDateError("終了日は開始日より後の日付を選択してください。");
    } else {
      setDateError("");
    }
  }, [watchValues.starts_at, watchValues.ends_at]);

  // 開始日が変更されたら終了日を更新
  useEffect(() => {
    if (watchValues.starts_at && watchValues.starts_at_day) {
      const startsAtDate = dayjs(watchValues.starts_at);
      const startDate = startsAtDate.date(parseInt(watchValues.starts_at_day));
      const endDate = startDate.add(2, "year").subtract(1, "day");

      setValue("ends_at", endDate.toDate());
      setValue("ends_at_day", endDate.format("D"));
    }
  }, [watchValues.starts_at, watchValues.starts_at_day, setValue]);

  // 選択肢
  return (
    <>
      <Box>
        <Box className={classes.content} data-registration-content>
          <Box className={classes.block}>
            {/* XXX号室 のタイトル */}
            <Box className={classes.block}>
              <Typography variant="h4" className={classes.blockTitle}>
                {formData.room_number ? `${formData.room_number}号室` : "号室未入力"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                2部屋目以降は物件詳細ページから登録できます
              </Typography>
            </Box>
            <Controller
              name="monthly_fee"
              control={control}
              defaultValue={formData.monthly_fee || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="月間家賃収入"
                  required
                  error={!!errors.monthly_fee}
                  errorText={errors.monthly_fee?.message}
                  onChange={field.onChange}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
            {/* その他月間収入 */}
            <Controller
              name="other_monthly_income"
              control={control}
              defaultValue={formData.other_monthly_income || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="その他月間収入（駐車場など）"
                  required
                  error={!!errors.other_monthly_income}
                  errorText={errors.other_monthly_income?.message}
                  onChange={field.onChange}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          </Box>

          <Box className={classes.block}>
            <Box mb={1}>
              <CustomFormLabel label="賃貸借契約の開始月" required info="購入した年月日を選択してください。" />
            </Box>

            <Controller
              name="starts_at"
              control={control}
              // MEMO: 自動で当月を入れない（2026-01-02）
              // defaultValue={toMonthPickerDate(formData.starts_at, dayjs().startOf("month").toDate())}
              defaultValue={toMonthPickerDate(formData.starts_at, null)}
              render={({ ...field }) => (
                <DatePickerComponent
                  required
                  {...field}
                  labelLight="年月"
                  error={!!errors.starts_at}
                  errorText={errors.starts_at?.message as string}
                  value={toMonthPickerDate(field.value, null)}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              name="starts_at_day"
              control={control}
              // MEMO: 自動で「1日」を入れない（2026-01-02）
              // defaultValue={formData.starts_at_day || "1"}
              defaultValue={formData.starts_at_day || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  labelLight="日にち"
                  helperText="不明の場合は値を「1日」のままお進みください。"
                  error={!!errors.starts_at_day}
                  errorText={errors.starts_at_day?.message}
                  onChange={field.onChange}
                  placeHolder="1"
                  unitSuffix="日"
                  isShort
                />
              )}
            />

            <Box mb={1}>
              <CustomFormLabel label="賃貸借契約の終了月" required info="購入した年月日を選択してください。" />
            </Box>

            <Controller
              name="ends_at"
              control={control}
              // MEMO: 自動で「当月+2年」を入れない（2026-01-02）
              // defaultValue={toMonthPickerDate(formData.ends_at, dayjs().add(2, "year").startOf("month").toDate())}
              defaultValue={toMonthPickerDate(formData.ends_at, null)}
              render={({ ...field }) => (
                <DatePickerComponent
                  required
                  labelLight="年月"
                  {...field}
                  error={!!errors.ends_at}
                  errorText={errors.ends_at?.message as string}
                  value={toMonthPickerDate(field.value, null)}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              name="ends_at_day"
              control={control}
              // MEMO: 自動で「1日」を入れない（2026-01-02）
              // defaultValue={formData.ends_at_day || "1"}
              defaultValue={formData.ends_at_day || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  labelLight="日にち"
                  helperText="不明の場合は値を「1日」のままお進みください。"
                  error={!!errors.ends_at_day}
                  errorText={errors.ends_at_day?.message}
                  onChange={field.onChange}
                  placeHolder="1"
                  unitSuffix="日"
                  isShort
                />
              )}
            />
            {dateError && (
              <Typography color="error" variant="body2">
                {dateError}
              </Typography>
            )}
          </Box>
        </Box>
        <RegistrationActions>
          <CustomButton type="button" customVariant="filled" onClick={handleNext}>
            次へ
          </CustomButton>
          <RegistrationBack onClick={onBack} />
        </RegistrationActions>
      </Box>
      <Toast open={!!serverError} variant="error" message={serverError} onClose={handleClose} />
    </>
  );
};

export default AddNewOwnBuildingPropertyRoomPage;
